// Folio de la reserva EN CASA: ventanas (cliente/empresa) con sus cargos y
// pagos, saldo, y acciones de caja (cargo extra/ajuste, anular, mover a otra
// ventana, crear ventana). Se usa dentro de ReservaDetalle.tsx; en estado
// "salida" se monta con soloLectura para mostrar las facturas ya emitidas.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { usePermissions } from '@/lib/hooks/usePermissions';
import { Campo, Panel, fieldCls, money, hotelRpc, QK, fmtFecha, type Folio as FolioData, type FolioVentana, useDialogoEnLinea } from './shared';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
interface PartnerOpt { id: string; razon_social: string; identificacion: string | null; tipo_partner: string[] }

export function Folio({ reservaId, soloLectura = false }: { reservaId: string; soloLectura?: boolean }) {
  const dlg = useDialogoEnLinea();
  const qc = useQueryClient();
  const { isAdmin, hasAccessLevel } = usePermissions();
  const { data: folio, isLoading } = useQuery<FolioData>({
    queryKey: [QK, 'folio', reservaId],
    queryFn: () => hotelRpc<FolioData>('folio_obtener', { _reserva: reservaId }),
    staleTime: 0, refetchOnMount: 'always',
  });

  const [cargoVentana, setCargoVentana] = useState<number | null>(null);
  const [nuevaVentana, setNuevaVentana] = useState(false);

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: [QK, 'folio', reservaId] });
    void qc.invalidateQueries({ queryKey: [QK] });
  };

  const anular = useMutation({
    mutationFn: ({ cargo, motivo }: { cargo: string; motivo: string }) => hotelRpc('cargo_anular', { _cargo: cargo, _motivo: motivo }),
    onSuccess: () => { toast.success('Cargo anulado'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const interina = useMutation({
    mutationFn: () => hotelRpc<{ facturas: { ventana: string; total: number; emision_error: string | null }[] }>('factura_interina', { _reserva: reservaId }),
    onSuccess: (r) => {
      const n = r.facturas?.length ?? 0;
      if (n === 0) toast.info('No hay consumos pendientes de facturar hasta la fecha de hotel');
      else toast.success(`${n} factura(s) parcial(es) emitida(s)`);
      r.facturas?.filter((f) => f.emision_error).forEach((f) => toast.warning(`${f.ventana}: ${f.emision_error}`));
      refrescar();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const dividir = useMutation({
    mutationFn: ({ cargo, ventana, pct }: { cargo: string; ventana: string; pct: number }) => hotelRpc('cargo_dividir', { _cargo: cargo, _ventana: ventana, _pct: pct }),
    onSuccess: () => { toast.success('Cargo dividido'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const [rutasVentana, setRutasVentana] = useState<FolioVentana | null>(null);
  const transferir = useMutation({
    mutationFn: ({ cargo, ventana }: { cargo: string; ventana: string }) => hotelRpc('cargo_transferir', { _cargo: cargo, _ventana: ventana }),
    onSuccess: () => { toast.success('Cargo movido de ventana'); refrescar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  if (isLoading || !folio) return <div className="py-6 text-center text-sm text-muted-foreground">Cargando folio…</div>;

  return (
    <div className="space-y-3">
      {dlg.ui}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm">
          Saldo del folio: <span className="font-semibold">{money(folio.saldo)}</span>
          {folio.limite_credito != null && <span className="ml-2 text-xs text-muted-foreground">límite de crédito {money(folio.limite_credito)}</span>}
        </div>
        {!soloLectura && (
          <div className="flex gap-2">
            {hasAccessLevel(4) && (
              <Button size="sm" variant="outline" isLoading={interina.isPending}
                title="Factura lo consumido hasta la fecha de hotel sin cerrar la cuenta (el cierre del último día del mes lo hace solo)"
                onClick={async () => { if (await dlg.confirmar('¿Facturar ahora lo consumido hasta la fecha de hotel?', { detalle: 'La cuenta sigue abierta.', aceptar: 'Facturar hasta hoy' })) interina.mutate(); }}>
                Facturar hasta hoy
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={() => setNuevaVentana(true)}>+ Ventana</Button>
          </div>
        )}
      </div>

      {cargoVentana != null && (
        <ModalCargo reservaId={reservaId} ventana={cargoVentana} onClose={() => setCargoVentana(null)} onDone={() => { setCargoVentana(null); refrescar(); }} />
      )}
      {rutasVentana && (
        <ModalRutas ventana={rutasVentana} onClose={() => setRutasVentana(null)} onDone={() => { setRutasVentana(null); refrescar(); }} />
      )}
      {nuevaVentana && (
        <ModalVentana reservaId={reservaId} onClose={() => setNuevaVentana(false)} onDone={() => { setNuevaVentana(false); refrescar(); }} />
      )}
      {folio.ventanas.map((v) => {
        const totalBase = v.cargos.filter((c) => !c.anulado).reduce((a, c) => a + Number(c.monto_base), 0);
        const totalIva = v.cargos.filter((c) => !c.anulado).reduce((a, c) => a + Number(c.iva_monto), 0);
        const totalCargos = v.cargos.filter((c) => !c.anulado).reduce((a, c) => a + Number(c.total), 0);
        const totalPagos = v.pagos.reduce((a, p) => a + Number(p.monto) - Number(p.devuelto ?? 0), 0);
        return (
          <Card key={v.id}>
            <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 pb-2">
              <CardTitle className="text-sm font-semibold">
                Ventana {v.numero} — {v.nombre}
                {v.partner && <span className="ml-2 text-xs font-normal text-muted-foreground">{v.partner}{v.identificacion ? ` (${v.identificacion})` : ''}</span>}
                <span className="ml-2 inline-flex flex-wrap gap-1 align-middle">
                  {(v.rutas ?? []).map((ru) => <span key={ru} className="rounded bg-violet-100 px-1.5 py-0.5 text-[10px] font-normal text-violet-800 dark:bg-violet-900/30 dark:text-violet-300">{RUTAS[ru] ?? ru}</span>)}
                  {!soloLectura && !v.ticket_id && <button type="button" className="text-[11px] font-normal text-primary underline" onClick={() => setRutasVentana(v)}>qué paga</button>}
                </span>
              </CardTitle>
              <div className="flex items-center gap-2 text-xs">
                {(v.facturas ?? []).map((fa) => (
                  <span key={fa.ticket_id} title={`Saldo ${money(fa.saldo)}`}
                    className={`rounded px-2 py-0.5 ${fa.parcial ? 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300' : 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'}`}>
                    {fa.parcial ? 'Parcial' : 'Factura'} {fa.serie ?? 'por emitir'} · {money(fa.total)}
                  </span>
                ))}
                <span>Saldo: <strong>{money(v.saldo)}</strong></span>
                {!soloLectura && <Button size="sm" variant="outline" onClick={() => setCargoVentana(v.numero)}>+ Cargo</Button>}
              </div>
            </CardHeader>
            <CardContent className="space-y-3 pt-0">
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b text-left text-muted-foreground">
                    <th className="py-1.5 pr-2">N°</th><th className="py-1.5 pr-2">Fecha</th><th className="py-1.5 pr-2">Descripción</th>
                    <th className="py-1.5 pr-2 text-right">Base</th><th className="py-1.5 pr-2 text-right">IVA</th><th className="py-1.5 pr-2 text-right">Total</th>
                    {!soloLectura && <th className="py-1.5 pr-2 w-40"></th>}
                  </tr></thead>
                  <tbody>
                    {v.cargos.length === 0 && <tr><td colSpan={soloLectura ? 6 : 7} className="py-3 text-center text-muted-foreground">Sin cargos</td></tr>}
                    {v.cargos.map((c, i) => (
                      <tr key={c.id} className={`border-b last:border-0 ${c.anulado ? 'text-muted-foreground line-through' : ''}`}>
                        <td className="py-1.5 pr-2">{i + 1}</td>
                        <td className="py-1.5 pr-2">{fmtFecha(c.fecha)}</td>
                        <td className="py-1.5 pr-2" title={c.anulado ? `Anulado: ${c.anulado_motivo ?? ''}` : ''}>{c.descripcion}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{money(c.monto_base)}</td>
                        <td className="py-1.5 pr-2 text-right tabular-nums">{money(c.iva_monto)}</td>
                        <td className="py-1.5 pr-2 text-right font-mono tabular-nums">{money(c.total)}</td>
                        {!soloLectura && (
                          <td className="py-1.5 pr-2">
                            {c.facturado && !c.anulado && <span className="text-[11px] text-sky-700 dark:text-sky-400">facturado</span>}
                            {!c.anulado && !c.facturado && (
                              <div className="flex items-center gap-1">
                                {folio.ventanas.length > 1 && (
                                  <select className={`${fieldCls} h-7 text-[11px]`} defaultValue="" disabled={transferir.isPending}
                                    onChange={(e) => { if (e.target.value) transferir.mutate({ cargo: c.id, ventana: e.target.value }); }}>
                                    <option value="">Mover a…</option>
                                    {folio.ventanas.filter((w) => w.numero !== v.numero && !w.ticket_id).map((w) => <option key={w.id} value={w.id}>Ventana {w.numero} — {w.nombre}</option>)}
                                  </select>
                                )}
                                {folio.ventanas.length > 1 && Number(c.monto_base) > 0 && (
                                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px]" isLoading={dividir.isPending}
                                    onClick={async () => {
                                      const otras = folio.ventanas.filter((w) => w.numero !== v.numero && !w.ticket_id);
                                      const r = await dlg.pedir('Dividir el cargo', [
                                        { key: 'num', label: `¿A qué cuenta pasa una parte? (${otras.map((w) => `${w.numero} = ${w.nombre}`).join(', ')})`, placeholder: String(otras[0]?.numero ?? '') },
                                        { key: 'pct', label: '¿Qué porcentaje del cargo pasa a esa cuenta? (1-99)', placeholder: '50' }], { aceptar: 'Dividir' });
                                      if (!r) return;
                                      const dest = otras.find((w) => String(w.numero) === r.num.trim());
                                      const pct = Number(r.pct);
                                      if (!dest) { toast.error('Esa cuenta no existe o ya está facturada'); return; }
                                      if (pct > 0 && pct < 100) dividir.mutate({ cargo: c.id, ventana: dest.id, pct }); else toast.error('El porcentaje debe estar entre 1 y 99');
                                    }}>
                                    Dividir
                                  </Button>
                                )}
                                {isAdmin() && (
                                  <Button size="sm" variant="outline" className="h-7 px-2 text-[11px] text-red-600"
                                    isLoading={anular.isPending}
                                    onClick={async () => { const r = await dlg.pedir('Anular el cargo', [{ key: 'motivo', label: 'Motivo de anulación (obligatorio)', minimo: 5 }], { aceptar: 'Anular' }); if (r) anular.mutate({ cargo: c.id, motivo: r.motivo.trim() }); }}>
                                    Anular
                                  </Button>
                                )}
                              </div>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                  {v.cargos.length > 0 && (
                    <tfoot><tr className="border-t font-semibold">
                      <td className="py-1.5 pr-2" colSpan={3}>Totales</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">{money(totalBase)}</td>
                      <td className="py-1.5 pr-2 text-right tabular-nums">{money(totalIva)}</td>
                      <td className="py-1.5 pr-2 text-right font-mono tabular-nums">{money(totalCargos)}</td>
                      {!soloLectura && <td></td>}
                    </tr></tfoot>
                  )}
                </table>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead><tr className="border-b text-left text-muted-foreground">
                    <th className="py-1.5 pr-2">N°</th><th className="py-1.5 pr-2">Fecha</th><th className="py-1.5 pr-2">Forma</th>
                    <th className="py-1.5 pr-2">Referencia</th><th className="py-1.5 pr-2 text-right">Monto</th>
                  </tr></thead>
                  <tbody>
                    {v.pagos.length === 0 && <tr><td colSpan={5} className="py-2 text-center text-muted-foreground">Sin pagos registrados</td></tr>}
                    {v.pagos.map((p, i) => (
                      <tr key={p.id} className="border-b last:border-0">
                        <td className="py-1.5 pr-2">{i + 1}</td>
                        <td className="py-1.5 pr-2">{fmtFecha(p.fecha)}</td>
                        <td className="py-1.5 pr-2 capitalize">{p.forma}</td>
                        <td className="py-1.5 pr-2">{p.referencia ?? '—'}</td>
                        <td className="py-1.5 pr-2 text-right font-mono tabular-nums">
                          {money(p.monto)}{Number(p.devuelto ?? 0) > 0 && <div className="text-[11px] text-amber-700 dark:text-amber-400">devuelto {money(Number(p.devuelto))}</div>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {v.pagos.length > 0 && (
                    <tfoot><tr className="border-t font-semibold">
                      <td className="py-1.5 pr-2" colSpan={4}>Total pagado</td>
                      <td className="py-1.5 pr-2 text-right font-mono tabular-nums">{money(totalPagos)}</td>
                    </tr></tfoot>
                  )}
                </table>
              </div>
            </CardContent>
          </Card>
        );
      })}

    </div>
  );
}

function ModalCargo({ reservaId, ventana, onClose, onDone }: { reservaId: string; ventana: number; onClose: () => void; onDone: () => void }) {
  const { isAdmin } = usePermissions();
  const [tipo, setTipo] = useState<'extra' | 'ajuste'>('extra');
  const [descripcion, setDescripcion] = useState('');
  const [montoBase, setMontoBase] = useState(0);
  const [ivaPct, setIvaPct] = useState(15);
  const [motivo, setMotivo] = useState('');

  const agregar = useMutation({
    mutationFn: () => hotelRpc('cargo_agregar', {
      _reserva: reservaId,
      _d: { tipo, descripcion: descripcion.trim(), monto_base: montoBase, iva_pct: ivaPct, ventana_numero: ventana, motivo: tipo === 'ajuste' ? motivo.trim() : null },
    }),
    onSuccess: () => { toast.success('Cargo agregado'); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const puedeGuardar = descripcion.trim().length > 0 && (tipo !== 'ajuste' || motivo.trim().length >= 5);

  return (
    <Panel title={`Agregar cargo — Ventana ${ventana}`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <Campo label="Tipo">
          <select className={fieldCls} value={tipo} onChange={(e) => setTipo(e.target.value as 'extra' | 'ajuste')}>
            <option value="extra">Consumo / extra</option>
            {isAdmin() && <option value="ajuste">Ajuste (administrador)</option>}
          </select>
        </Campo>
        <Campo label="Descripción *"><input className={fieldCls} value={descripcion} onChange={(e) => setDescripcion(e.target.value)} /></Campo>
        <div className="grid grid-cols-2 gap-2">
          <Campo label="Monto base"><input type="number" step="0.01" className={fieldCls} value={montoBase} onChange={(e) => setMontoBase(Number(e.target.value) || 0)} /></Campo>
          <Campo label="IVA %"><input type="number" step="0.01" className={fieldCls} value={ivaPct} onChange={(e) => setIvaPct(Number(e.target.value) || 0)} /></Campo>
        </div>
        {tipo === 'ajuste' && (
          <Campo label="Motivo del ajuste * (mín. 5 caracteres)"><textarea className={`${fieldCls} h-auto`} rows={2} value={motivo} onChange={(e) => setMotivo(e.target.value)} /></Campo>
        )}
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={agregar.isPending} disabled={!puedeGuardar} onClick={() => agregar.mutate()}>Agregar</Button>
        </div>
      </div>
    </Panel>
  );
}

function ModalVentana({ reservaId, onClose, onDone }: { reservaId: string; onClose: () => void; onDone: () => void }) {
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['fin-partners'], queryFn: async () => (await apiClient.post<PartnerOpt[]>('/rpc/sri_partners', {}, FIN)).data || [] });
  const clientes = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('cliente')), [partners]);
  const [nombre, setNombre] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [rutas, setRutas] = useState<string[]>([]);

  const crear = useMutation({
    mutationFn: () => hotelRpc('ventana_crear', { _reserva: reservaId, _nombre: nombre.trim(), _partner: partnerId || null, _rutas: rutas }),
    onSuccess: () => { toast.success('Ventana creada'); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Panel title="Nueva ventana del folio" onClose={onClose}>
      <div className="space-y-3 text-sm">
        <Campo label="Nombre *"><input className={fieldCls} value={nombre} onChange={(e) => setNombre(e.target.value)} placeholder="Ej. Bar, Empresa XYZ…" /></Campo>
        <Campo label="Facturar a (opcional)">
          <select className={fieldCls} value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
            <option value="">— El huésped de la reserva —</option>
            {clientes.map((c) => <option key={c.id} value={c.id}>{c.razon_social} ({c.identificacion ?? 's/i'})</option>)}
          </select>
        </Campo>
        <SelectorRutas rutas={rutas} onChange={setRutas} />
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={crear.isPending} disabled={!nombre.trim()} onClick={() => crear.mutate()}>Crear</Button>
        </div>
      </div>
    </Panel>
  );
}

// Re-export de tipo útil para quien componga pantallas con la ventana del folio.
export type { FolioVentana };

// ---------------------------------------------------------------------------
// Rutas de cargos: qué tipos de consumo caen en cada cuenta (script 933)
const RUTAS: Record<string, string> = {
  hospedaje: 'Alojamiento', servicio: 'Servicio 10 %', tasa: 'Tasa turística', pos: 'Restaurante / bar / POS',
  extra: 'Extras manuales', ayb: 'Alimentos del plan', paquete: 'Tours / spa del plan',
};

function SelectorRutas({ rutas, onChange }: { rutas: string[]; onChange: (r: string[]) => void }) {
  return (
    <div className="space-y-1">
      <span className="text-xs font-medium text-muted-foreground">Esta cuenta paga (lo no marcado va a la cuenta del huésped)</span>
      <div className="grid grid-cols-2 gap-1">
        {Object.entries(RUTAS).map(([k, l]) => (
          <label key={k} className="flex items-center gap-2 text-xs">
            <input type="checkbox" checked={rutas.includes(k)} onChange={(e) => onChange(e.target.checked ? [...rutas, k] : rutas.filter((x) => x !== k))} /> {l}
          </label>
        ))}
      </div>
    </div>
  );
}

function ModalRutas({ ventana, onClose, onDone }: { ventana: FolioVentana; onClose: () => void; onDone: () => void }) {
  const [rutas, setRutas] = useState<string[]>(ventana.rutas ?? []);
  const [mover, setMover] = useState(true);
  const guardar = useMutation({
    mutationFn: () => hotelRpc<{ cargos_movidos: number }>('ventana_rutas', { _ventana: ventana.id, _rutas: rutas, _mover: mover }),
    onSuccess: (r) => { toast.success(`Rutas guardadas${r.cargos_movidos ? ` · ${r.cargos_movidos} cargo(s) movido(s)` : ''}`); onDone(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Panel title={`¿Qué paga «${ventana.nombre}»?`} onClose={onClose}>
      <div className="space-y-3 text-sm">
        <SelectorRutas rutas={rutas} onChange={setRutas} />
        <p className="text-xs text-muted-foreground">Cada tipo de consumo va a una sola cuenta: si lo marca aquí, se quita de las demás.</p>
        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={mover} onChange={(e) => setMover(e.target.checked)} /> Mover también los cargos ya registrados (sin facturar) de esos tipos</label>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={guardar.isPending} onClick={() => guardar.mutate()}>Guardar</Button>
        </div>
      </div>
    </Panel>
  );
}
